export interface DeviceOption {
  deviceId: string;
  label: string;
}

export async function listDevices(kind: 'audioinput' | 'videoinput'): Promise<DeviceOption[]> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((device) => device.kind === kind)
    .map((device, index) => ({
      deviceId: device.deviceId,
      label: device.label || `${kind === 'audioinput' ? 'Microphone' : 'Camera'} ${index + 1}`,
    }));
}

export async function openMicrophone(deviceId?: string): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: {
      deviceId: deviceId ? { exact: deviceId } : undefined,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    },
    video: false,
  });
}

export async function openWebcam(deviceId?: string): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: {
      deviceId: deviceId ? { exact: deviceId } : undefined,
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30, max: 30 },
    },
    audio: false,
  });
}

export interface DisplayCaptureRequest {
  fps: number;
  wantSystemAudio: boolean;
  /** Keep this tab out of the picker so the recorder never records itself. */
  hideOwnTab: boolean;
}

export async function openDisplay(request: DisplayCaptureRequest): Promise<MediaStream> {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: {
      frameRate: { ideal: request.fps, max: request.fps },
      width: { ideal: 4096 },
      height: { ideal: 2304 },
    },
    audio: request.wantSystemAudio
      ? {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        }
      : false,
    // Chrome-specific hints; ignored elsewhere. Note that `selfBrowserSurface`
    // and `preferCurrentTab` are mutually exclusive — asking for both makes
    // Chrome reject the whole request as self-contradictory.
    ...({
      selfBrowserSurface: request.hideOwnTab ? 'exclude' : 'include',
      surfaceSwitching: 'include',
      systemAudio: request.wantSystemAudio ? 'include' : 'exclude',
      cursor: 'always',
    } as Record<string, unknown>),
  } as DisplayMediaStreamOptions);

  const videoTrack = stream.getVideoTracks()[0];
  if (videoTrack) {
    // Screen content is text-heavy: ask the encoder pipeline to preserve detail.
    try {
      videoTrack.contentHint = 'detail';
    } catch {
      // Not implemented everywhere; harmless.
    }
  }
  return stream;
}

export function stopStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  for (const track of stream.getTracks()) track.stop();
}
